// Pure logic behind the sigils surfaces — the Home progression strip and the
// gallery. No fetching, no rendering: everything here is a plain function over the
// GET /sigils rows, testable with plain objects.

import type { Sigil } from '@/api/sigils'

/** The four bands the gallery draws, in reading order. */
export interface SigilGroups {
  /** Already yours — glyph bright, earned_at shown. */
  earned: Sigil[]
  /** Unearned, same-app, near-term — the low-rarity marks a keeper can reach by using Diwa. */
  inReach: Sigil[]
  /** Unearned, same-app, harder — high-rarity or founder-granted marks. */
  locked: Sigil[]
  /** Dormant cross-app marks (Sanctum, Eden) — light up when that app ships. */
  comingSoon: Sigil[]
}

/** Rarities we treat as "in reach" for an active keeper; anything rarer is locked. */
const IN_REACH_RARITY = new Set(['common', 'uncommon'])

/**
 * Split the flat GET /sigils list into the four display bands.
 *
 * Rules, in order (each sigil lands in exactly one band):
 *  1. earned === true                    → earned
 *  2. cross_app (and not earned)         → comingSoon  (dormant sibling-app marks)
 *  3. rarity common|uncommon             → inReach
 *  4. otherwise                          → locked      (rare+ or founder-granted)
 *
 * Order within each band is preserved from the server list (it decides ranking).
 */
export function groupSigils(sigils: Sigil[]): SigilGroups {
  const groups: SigilGroups = { earned: [], inReach: [], locked: [], comingSoon: [] }
  for (const s of sigils) {
    if (s.earned) groups.earned.push(s)
    else if (s.cross_app) groups.comingSoon.push(s)
    else if (IN_REACH_RARITY.has(s.rarity.toLowerCase())) groups.inReach.push(s)
    else groups.locked.push(s)
  }
  return groups
}

/** The three states a single sigil renders in — drives its chrome and its tooltip. */
export type SigilState = 'earned' | 'coming' | 'locked'

export function sigilState(s: Sigil): SigilState {
  if (s.earned) return 'earned'
  if (s.cross_app) return 'coming'
  return 'locked'
}

/**
 * The hover copy for one sigil, resolved from its state:
 *  - earned : its gloss (what it means)
 *  - coming : its locked_reason ("coming with Sanctum") or a sensible default
 *  - locked : how to earn it — locked_reason if the server gave one, else the gloss
 */
export function sigilHint(s: Sigil): string {
  const state = sigilState(s)
  if (state === 'earned') return s.gloss
  if (state === 'coming') return s.locked_reason ?? 'Coming with a sibling app in the suite.'
  return s.locked_reason ? `How to earn: ${s.locked_reason}` : s.gloss
}

/**
 * The earned strip on Home: earned sigils first (brightest), then a few locked ones as
 * the visible "next to earn" — capped so the strip never sprawls. Coming-soon marks are
 * excluded here; they live in the gallery. Returns the sigils in display order.
 */
export function homeStripSigils(sigils: Sigil[], lockedShown = 4): Sigil[] {
  const g = groupSigils(sigils)
  return [...g.earned, ...g.inReach.slice(0, lockedShown)]
}

/**
 * Current activity streak: consecutive days with any activity, counting back from the
 * most recent day in the series. The series is the zero-filled daily history (ascending),
 * so a gap of a single empty day ends the streak. `total > 0` is "active".
 */
export function currentStreak(days: { day: string; total: number }[]): number {
  let streak = 0
  for (let i = days.length - 1; i >= 0; i -= 1) {
    if (days[i].total > 0) streak += 1
    else break
  }
  return streak
}

/** "3 earned · 2 in reach" — the one-line count under the progression header. */
export function sigilSummary(sigils: Sigil[]): string {
  const g = groupSigils(sigils)
  const earned = g.earned.length
  const reach = g.inReach.length
  if (sigils.length === 0) return 'No sigils yet.'
  if (earned === 0) return `${reach} sigil${reach === 1 ? '' : 's'} within reach — start earning.`
  return `${earned} earned${reach > 0 ? ` · ${reach} in reach` : ''}`
}
